import React, { useState } from "react";
import { Button } from "antd";
import { Eye } from "lucide-react";
import InfoComment from "./InfoComment";
import { EyeOutlined } from "@ant-design/icons";
import { defaultColors } from '@/themes/antd-theme';
import { formatDateTime } from '@/utils/datetime';
import {
  Table,
  Input,
  Modal,
  Popover,
  Select,
  Descriptions,
  Pagination,
  Form,
  Tag,
  Badge,Typography,
  Tooltip,
  Spin,
  Empty,
  Popconfirm,
  Collapse,
} from "antd";
const { Panel } = Collapse;
const ActionCModal = ({ isOpen, onClose, modalData }) => {
  // additional_field is a JSONField — it can arrive as an object, a JSON string,
  // an array, a primitive, or null. Normalize to an object for display.
  let parsed = modalData;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch (_e) {
      // not JSON — keep the raw string
    }
  }

  const isObject =
    parsed && typeof parsed === "object" && !Array.isArray(parsed);
  const entries = isObject
    ? Object.entries(parsed).filter(([key]) => key !== "options")
    : [];
  const options = isObject && Array.isArray(parsed.options) ? parsed.options : [];

  const hasData =
    (parsed !== null && parsed !== undefined && parsed !== "") &&
    (entries.length > 0 || options.length > 0 || !isObject);

  return (
    <Modal
      title="Additional Data"
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={600}
      zIndex={3000}
    >
      {!hasData ? (
        <Empty description="No additional data" />
      ) : !isObject ? (
        // Primitive / array value — render as-is.
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="Value">
            {Array.isArray(parsed)
              ? parsed
                  .map((v) => (typeof v === "object" && v !== null ? JSON.stringify(v) : String(v)))
                  .join(", ")
              : String(parsed)}
          </Descriptions.Item>
        </Descriptions>
      ) : (
        <>
          {entries.length > 0 && (
            <Descriptions bordered column={1} size="small">
              {entries.map(([key, value]) => (
                <Descriptions.Item label={key} key={key}>
                  {typeof value === "boolean"
                    ? value
                      ? "Yes"
                      : "No"
                    : Array.isArray(value)
                    ? value
                        .map((v) => (typeof v === "object" && v !== null ? JSON.stringify(v) : String(v)))
                        .join(", ")
                    : typeof value === "object" && value !== null
                    ? JSON.stringify(value)
                    : String(value ?? "-")}
                </Descriptions.Item>
              ))}
            </Descriptions>
          )}

          {options.length > 0 && (
            <>
              <h4 className="mt-4 mb-2 font-semibold text-gray-700">Options</h4>
              <Table
                dataSource={options}
                columns={[
                  { title: "Label", dataIndex: "label", key: "label" },
                  { title: "Value", dataIndex: "value", key: "value" },
                ]}
                rowKey={(r, i) => r?.value ?? i}
                pagination={false}
                size="small"
                bordered
              />
            </>
          )}
        </>
      )}
    </Modal>
  );
};

const ingredientStrings = (data, header) => {
  return data?.map((ingredient, index) => {
    let ingredientString = `${ingredient.material_name} `;
    if (header === "operation") {
      // Conditionally include the parameter with its min and max values if they exist
      ingredientString += "";
      // ingredient.parameter_name && `(${ingredient.parameter_name}), `;"
    } else {
      ingredientString += `: ${ingredient.quantity + ""}`;
      if (ingredient.parameter_name !== "") {
        ingredientString += `-${ingredient.parameter_name}: Min ${ingredient.min_value} ${ingredient.parameter_name_unit} and Max ${ingredient.max_value} ${ingredient.parameter_name_unit}`;
      } else {
        ingredientString += `-`;
      }
    }
    ingredientString += index === data?.length - 1 ? "" : ",";
    return ingredientString;
  });
};

export const getOperationColumns = ({ t }) => [
  {
    title: "Operation Number",
    dataIndex: "operation_number",
    key: "operation",
    align: "center",
  },
  {
    title: "Operation",
    dataIndex: "operation_type",
    key: "operation_type",
    align: "center",
    render: (data, record) => {
      const ingred = ingredientStrings(record.ingredients, "operation");

      return (
        <div style={{ textAlign: "left" }}>
          <p>
            <strong>Operation Type:</strong> {`${data} `}
            <br />
            {record?.operation_name && (
              <>
                <strong> Operation : </strong>
                <span>{`${record?.operation_name} `}</span> <br />
              </>
            )}
            {record?.instruction?.length > 0 && (
              <>
                <strong>{t("Instruction")}:</strong>
                <Tooltip title={`View ${t("Instruction")}`}>
                  <Button
                    type="text"
                    size="small"
                    icon={<Eye size={18} style={{ color: defaultColors.info }} />}
                    onClick={() => {
                      Modal.info({
                        title: t("Instruction"),
                        content: (
                          <ul className="space-y-2">
                            {record?.instruction?.map((ins, idx) => (
                              <li key={idx} className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  id={`ins-${idx}`}
                                  checked
                                  className="cursor-pointer"
                                />
                                <label
                                  htmlFor={`ins-${idx}`}
                                  className="cursor-pointer"
                                >
                                  {ins}
                                </label>
                              </li>
                            ))}
                          </ul>
                        ),
                      });
                    }}
                  />
                </Tooltip>
              </>
            )}
            {record?.notes_list?.length > 0 && (
              <div className="mt-[-2]">
                <strong>{t("Notes")}:</strong>
                <Tooltip title={t(`View ${t("Notes")}`)}>
                  <Button
                    type="text"
                    size="small"
                    icon={<Eye size={18} style={{ color: defaultColors.info }} />}
                    onClick={() => {
                      Modal.info({
                        title: t("Notes"),
                        content: (
                          <ul>
                            {record?.notes_list?.map((ins, idx) => (
                              <li key={idx}>{ins}</li>
                            ))}
                          </ul>
                        ),
                      });
                    }}
                  />
                </Tooltip>
                <br />
              </div>
            )}
            {record?.dependencie && (
              <>
                <strong>Depending on Operations:</strong>{" "}
                <span>{`${record?.dependencie}`}</span>
                <br />
              </>
            )}
            {ingred?.length > 0 && (
              <>
                <strong>Ingredients:</strong>
                <div className=" ml-5 max-h-44 overflow-y-auto">
                  {ingred?.map((ingredient, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontSize: 20, marginRight: 8 }}>•</span>
                      <span>{ingredient}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </p>
        </div>
      );
    },
  },
  {
    title: "Equipment",
    dataIndex: "machine_type",
    align: "center",
    key: "machine_type",
    render: (data, record) =>
      data?.includes(",") ? (
        <Tooltip title="View Machines">
          <Button
            type="text"
            size="small"
            icon={<Eye size={18} style={{ color: defaultColors.info }} />}
            onClick={() => {
              Modal.info({
                title: "Machines",
                content: (
                  <ul>
                    {record?.machine_type?.split(",").map((type, idx) => (
                      <li key={idx}>{type}</li>
                    ))}
                  </ul>
                ),
              });
            }}
          />
        </Tooltip>
      ) : (
        <span>{data}</span>
      ),
  },
  {
    title: "Duration",
    dataIndex: "max_duration",
    key: "max_duration",
    align: "center",
  },
  {
    title: "Details / Remarks",
    dataIndex: "products",
    key: "process_products",
    width: "25%",
    align: "center",
    render: (data, record) => {
      const ingred = ingredientStrings(record?.ingredients, "");
      const mul = "X Batch Size";
      const perc = "% of Batch Size";

      return (
        <div style={{ textAlign: "left" }}>
          {ingred?.length > 0 && (
            <>
              <strong>Ingredients:</strong>

              <div className="mt-2 max-h-44 overflow-y-auto">
                {ingred.map((line, index) => {
                  const parts = line?.split("-");
                  return (
                    <div key={index}>
                      <div>
                        <strong>{`${index + 1}. `}</strong>
                        {parts.length > 2
                          ? `${parts[0]}-${parts[1]}`
                          : parts[0]}
                        {record?.ingredients[index]?.is_percentage
                          ? ` ${perc}`
                          : ` ${record?.ingredients[index]?.material_unit} ${mul}`}
                        <InfoComment
                          title="Instruction"
                          content={record?.ingredients[index]?.instruction}
                        />
                      </div>
                      {parts[1] && parts[1].length > 1 && parts.length < 3 && (
                        <div>
                          <strong>{parts[1].split(":")[0]}:</strong>{" "}
                          {parts[1].split(":")[1]}
                        </div>
                      )}
                      {parts[2] && parts[2].length > 1 && (
                        <div>
                          <strong>{parts[2].split(":")[0]}:</strong>{" "}
                          {parts[2].split(":")[1]}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      );
    },
  },
];

export const ViewOperationDetailsCollapse = ({ record }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);

  const showModal = (data) => {
    setModalData(data);
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setModalData(null);
  };

  return (
    <div>
      <Collapse accordion>
        {record?.ingredients?.length > 0 && (
          <>
            <Panel header="Ingredients" key="1">
              <Table
                size="small"
                pagination={false}
                columns={[
                  {
                    title: "Material Name",
                    dataIndex: "material_name",
                    key: "material_name",
                  },
                  {
                    title: "Quantity",
                    dataIndex: "quantity",
                    key: "quantity",
                    render: (data, record, index) => {
                      return <p>{`${data} (${record?.material_unit})`}</p>;
                    },
                  },
                  {
                    title: "Parameter",
                    dataIndex: "parameter_name",
                    key: "parameter_name",
                    render: (data, record) => {
                      return !data ? (
                        "NA"
                      ) : (
                        <p>{`${data} (${
                          record?.parameter_name_unit || ""
                        })`}</p>
                      );
                    },
                  },
                  {
                    title: "Min Value",
                    dataIndex: "min_value",
                    key: "min_value",
                    render: (data, record, index) => {
                      return (
                        <p>{`${data} (${record?.parameter_name_unit})`}</p>
                      );
                    },
                  },
                  {
                    title: "Max Value",
                    dataIndex: "max_value",
                    key: "max_value",
                    render: (data, record, index) => {
                      return (
                        <p>{`${data} (${record?.parameter_name_unit})`}</p>
                      );
                    },
                  },
                  {
                    title: "Instruction",
                    dataIndex: "instruction",
                    key: "instruction",
                    align: "center",
                    render: (data, record) => {
                      return (
                        <p>
                          <InfoComment
                            title="Instruction"
                            content={record?.instruction}
                          />
                        </p>
                      );
                    },
                  },
                ]}
                dataSource={record?.ingredients}
                bordered
                style={{ width: "100%" }}
              />
            </Panel>
          </>
        )}

        {record?.process_products?.length > 0 && (
          <>
            <Panel header="Product" key="1">
              <Table
                size="small"
                pagination={false}
                columns={[
                  {
                    title: "Product Name",
                    dataIndex: "product_name",
                    key: "product_name",
                  },
                  {
                    title: "Process Thero Quantity",
                    dataIndex: "process_thero_quantity",
                    key: "operation_number",
                    render: (data, record, index) => {
                      return <p>{`${data} (${record.product_name_unit})`}</p>;
                    },
                  },
                  {
                    title: "Expected Min Value",
                    dataIndex: "process_expec_min_value",
                    key: "process_expec_min_value",
                    render: (data, record, index) => {
                      return <p>{`${data} (${record.product_name_unit})`}</p>;
                    },
                  },
                  {
                    title: "Expected Max Value",
                    dataIndex: "process_expec_max_value",
                    key: "process_expec_max_value",
                    render: (data, record, index) => {
                      return <p>{`${data} (${record.product_name_unit})`}</p>;
                    },
                  },
                  {
                    title: "Instruction",
                    dataIndex: "instruction",
                    key: "instruction",
                    align: "center",
                    render: (data, record) => {
                      return (
                        <p>
                          <InfoComment
                            title="Instruction"
                            content={record?.instruction}
                          />
                        </p>
                      );
                    },
                  },
                ]}
                dataSource={record?.process_products}
                bordered
                style={{ width: "100%" }}
              />
            </Panel>
          </>
        )}

        {record?.process_parameters &&
          record?.process_parameters?.length > 0 && (
            <>
              <Panel header="Process Parameters" key="2">
                <Table
                  size="small"
                  pagination={false}
                  columns={[
                    {
                      title: "Parameter",
                      dataIndex: "parameter_name",
                      rowScope: "row",
                    },
                    {
                      title: "Readings Per Interval (HH:MM:SS)",
                      dataIndex: "parameter_max_value",
                      key: "parameter_max_value",
                      render: (data, record, index) => {
                        return <p>{`${data}`}</p>;
                      },
                    },
                    {
                      title: "Quantity",
                      dataIndex: "qunatity",
                      key: "qunatity",
                      align: "center",
                      render: (data, record) => {
                        return (
                          <p>{`${data} (${record?.parameter_name_unit})`}</p>
                        );
                      },
                    },
                    {
                      title: "Readings",
                      dataIndex: "reading",
                      key: "min_value",
                      align: "center",
                      render: (data, record) => {
                        return (
                          <p>
                            {data
                              ? `${data}`
                              : [
                                  record?.is_start ? "At Start" : null,
                                  record?.is_end ? "At End" : null,
                                ]
                                  .filter(Boolean) // Remove any null values
                                  .join(", ")}
                          </p>
                        );
                      },
                    },
                    {
                      title: "Min",
                      dataIndex: "min_value",
                      key: "min_value",
                      align: "center",
                      render: (data, record) => {
                        return (
                          <p>
                            {data
                              ? `${data} (${record?.parameter_name_unit})`
                              : "NA"}
                          </p>
                        );
                      },
                    },
                    {
                      title: "Max",
                      dataIndex: "max_value",
                      key: "max_value",
                      align: "center",
                      render: (data, record) => {
                        return (
                          <p>
                            {data
                              ? `${data} (${record?.parameter_name_unit})`
                              : "NA"}
                          </p>
                        );
                      },
                    },
                    {
                      title: "Auto Status",
                      dataIndex: "auto_status",
                      key: "auto_status",
                      align: "center",
                      render: (data, record) => {
                        return <p>{data ? `${"Auto"}` : "Manual"}</p>;
                      },
                    },
                    {
                      title: "Sensor",
                      dataIndex: "sensor_name",
                      key: "sensor_name",
                      align: "center",
                      render: (data, record) => {
                        return <p>{data}</p>;
                      },
                    },
                    {
                      title: "Instruction",
                      dataIndex: "instruction",
                      key: "instruction",
                      align: "center",
                      render: (data, record) => {
                        return (
                          <p>
                            <InfoComment
                              title="Instruction"
                              content={record?.instruction}
                            />
                          </p>
                        );
                      },
                    },
                  ].filter(Boolean)}
                  dataSource={record.process_parameters}
                  bordered
                  style={{ width: "100%" }}
                />
              </Panel>
            </>
          )}
        {record?.sampling && record?.sampling?.length > 0 && (
          <Panel header="Sampling" key="3">
            <Table
              size="small"
              pagination={false}
              columns={[
                {
                  title: "Output",
                  align: "center",
                  dataIndex: "material_inventory_name",
                  key: "material_inventory_name",
                },
                {
                  title: "Quantity",
                  align: "center",
                  dataIndex: "quantity",
                  key: "quantity",
                  render: (data, record, index) => {
                    return (
                      <p>{`${data} (${record.material_inventory_unit})`}</p>
                    );
                  },
                },
                {
                  title: "Testing Type",
                  align: "center",
                  dataIndex: "testing_type_name",
                  key: "testing_type_name",
                },
                {
                  title: "Parameter",
                  align: "center",
                  dataIndex: "conditional_param",
                  key: "parameter",
                  render: (data, record) => {
                    return data[0]?.parameter ? data[0].parameter : "NA";
                  },
                },
                {
                  title: "Min",
                  align: "center",
                  dataIndex: "conditional_param",
                  key: "min_value",
                  render: (data) => {
                    return data[0]?.min_value ? data[0].min_value : "NA";
                  },
                },
                {
                  title: "Max",
                  align: "center",
                  dataIndex: "conditional_param",
                  key: "max_value",
                  render: (data) => {
                    return data[0]?.max_value ? data[0].max_value : "NA";
                  },
                },
                {
                  title: "Instruction",
                  dataIndex: "instruction",
                  key: "instruction",
                  align: "center",
                  render: (data, record) => {
                    return (
                      <p>
                        <InfoComment
                          title="Instruction"
                          content={record?.instruction}
                        />
                      </p>
                    );
                  },
                },
              ]}
              dataSource={record.sampling}
              bordered
              style={{ width: "100%" }}
            />
          </Panel>
        )}
        {record?.line_clearance_data &&
          record?.line_clearance_data?.length > 0 && (
            <Panel header="Line Clearance" key="4">
              <Table
                size="small"
                pagination={false}
                columns={[
                  {
                    title: "Clearance Type",
                    dataIndex: "clearance_type",
                    key: "clearance_type",
                  },
                  {
                    title: "Expected Value",
                    dataIndex: "expected_value",
                    key: "expected_value",
                  },
                  {
                    title: "Cleaning Types",
                    dataIndex: "cleaning_type_list",
                    key: "cleaning_type_list",
                    render: (types) => {
                      if (!Array.isArray(types)) return null;

                      return (
                        <div className="flex flex-wrap gap-2">
                          {types?.map((t, index) => (
                            <Tag key={index} color="blue">
                              {t?.cleaning_type_name || "NA"}
                            </Tag>
                          ))}
                        </div>
                      );
                    },
                  },

                  {
                    title: "Instruction",
                    dataIndex: "instruction",
                    key: "instruction",
                  },
                  {
                    title: "Remark",
                    dataIndex: "remark",
                    key: "remark",
                    render: (text) => text || "-", // Optional: show dash if no remark
                  },
                ]}
                dataSource={record?.line_clearance_data}
                bordered
                style={{ width: "100%" }}
              />
            </Panel>
          )}
        {record?.api_calculation_data &&
          record?.api_calculation_data?.length > 0 && (
            <Panel header="Api Calculations" key="5">
              <Table
                size="small"
                pagination={false}
                columns={[
                  {
                    title: "Calculation Field",
                    dataIndex: "calculation_field",
                    key: "calculation_field_id",
                  },
                  {
                    title: "Expected Value",
                    dataIndex: "expected_value",
                    key: "expected_value",
                  },
                  {
                    title: "Spec Min",
                    dataIndex: "spec_min",
                    key: "spec_min",
                  },
                  {
                    title: "Spec Max",
                    dataIndex: "spec_max",
                    key: "spec_max",
                  },
                  {
                    title: "Unit",
                    dataIndex: "unit",
                    key: "unit",
                  },
                  {
                    title: "Formula",
                    dataIndex: "formula",
                    key: "formula",
                  },
                  {
                    title: "Instruction",
                    dataIndex: "instruction",
                    key: "instruction",
                  },
                ]}
                dataSource={record?.api_calculation_data}
                bordered
                style={{ width: "100%" }}
              />
            </Panel>
          )}
        {record?.machines && record?.machines?.length > 0 && (
          <Panel header="Machines" key="6">
            <Table
              size="small"
              pagination={false}
              columns={[
                {
                  title: "Machine Name",
                  dataIndex: "machine_name",
                  key: "machine_name",
                },
              ]}
              dataSource={record?.machines}
              bordered
              style={{ width: "100%" }}
            />
          </Panel>
        )}
        {record?.action_list && record?.action_list?.length > 0 && (
          <>
            <Panel header="Action List" key="7">
              <Table
                size="small"
                pagination={false}
                columns={[
                  {
                    title: "Label",
                    dataIndex: "label_name",
                    key: "label_name",
                  },
                  {
                    title: "Data Type",
                    dataIndex: "data_type",
                    key: "data_type",
                  },
                  {
                    title: "Additional Data",
                    dataIndex: "additional_data",
                    key: "additional_data",
                    align: "center",
                    render: (add) => (
                      <EyeOutlined
                        className="cursor-pointer text-blue-500 text-lg hover:text-blue-400 hover:scale-110 transition-transform duration-200"
                        onClick={() => showModal(add)}
                      />
                    ),
                  },
                ]}
                dataSource={record?.action_list}
                bordered
                style={{ width: "100%" }}
              />
            </Panel>
          </>
        )}
        {record?.instruction && record?.instruction?.length > 0 && (
          <Panel header="CheckList" key="8">
            <div
              style={{
                maxHeight: "200px", // limit visible height
                overflowY: "auto", // add vertical scroll if content exceeds height
                paddingRight: "8px",
              }}
            >
              {record?.instruction?.map((item, index) => (
                <p
                  key={index}
                  style={{
                    marginBottom: "8px",
                    lineHeight: "1.5",
                    color: "#374151", // gray-700
                  }}
                >
                  <strong>{index + 1}.</strong> {item}
                </p>
              ))}
            </div>
          </Panel>
        )}
        {record?.notes_list && record?.notes_list?.length > 0 && (
          <Panel header="Note list" key="8">
            <div
              style={{
                maxHeight: "200px", // limit visible height
                overflowY: "auto", // add vertical scroll if content exceeds height
                paddingRight: "8px",
              }}
            >
              {record?.notes_list?.map((item, index) => (
                <p
                  key={index}
                  style={{
                    marginBottom: "8px",
                    lineHeight: "1.5",
                    color: "#374151", // gray-700
                  }}
                >
                  <strong>{index + 1}.</strong> {item}
                </p>
              ))}
            </div>
          </Panel>
        )}
      </Collapse>
      <ActionCModal
        isOpen={isModalOpen}
        onClose={handleCancel}
        modalData={modalData}
      />
    </div>
  );
};

export const FinalizedOperationDetailsCollapse = ({ record }) => {
  const deviation_color = (per) => {
    let color;
    let bg_color;

    switch (true) {
      case per === "":
        break;
      case per >= 0 && per <= 5:
        color = "green";
        bg_color = "#CCFFCC ";
        break;
      case (per >= -10 && per < 0) || (per > 5 && per <= 50):
        color = "orange";
        bg_color = "#FFF59E";
        break;
      case per < -10 || per > 50:
        color = "red";
        bg_color = "#FFCCCC";
        break;
      default:
        color = "black";
    }
    return { color: color, bg_color: bg_color };
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);

  const showModal = (data) => {
    setModalData(data);
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setModalData(null);
  };

  return (
    <div>
      <Collapse accordion>
        {record?.detailsRemarks?.batch_ingredients?.length > 0 && (
          <Panel header="Batch Ingredients" key="1">
            <>
              <Typography variant="h5">Batch Ingredients</Typography>
              <Table
                dataSource={record?.detailsRemarks?.batch_ingredients}
                size="small"
                columns={[
                  {
                    title: "Date-Time",
                    dataIndex: "created_on",
                    key: "created_on",
                    render: (created_on) => formatDateTime(created_on),
                  },
                  {
                    title: "Ingredient",
                    dataIndex: "ingredients_name",
                    key: "ingredients_name",
                    render: (_, record) => `${record?.ingredients_name}`,
                  },
                  {
                    title: "Expected Quantity",
                    dataIndex: "expected_quantity",
                    key: "expected_quantity",
                    render: (_, record) =>
                      `${record?.expected_quantity} ${record?.unit}`,
                  },
                  {
                    title: "Quantity",
                    dataIndex: "quantity",
                    key: "quantity",
                    render: (_, record) =>
                      record?.quantity ? record.quantity + record?.unit : "",
                  },
                  {
                    title: "Quantity Deviation",
                    dataIndex: "quantity_deviation",
                    key: "quantity_deviation",
                    width: "20%",
                    render: (_, record) => {
                      if (record.quantity_deviation === "") {
                        return "";
                      } else {
                        return record.quantity_deviation + " %";
                      }
                    },
                    onCell: (record) => ({
                      style: {
                        backgroundColor: deviation_color(
                          record?.quantity_deviation
                        )?.bg_color,
                        color: deviation_color(record?.quantity_deviation)
                          ?.color,
                      },
                    }),
                  },
                ]}
                pagination={false}
                bordered
              />
            </>
          </Panel>
        )}

        {record?.detailsRemarks?.batch_sample_value?.length > 0 && (
          <Panel header="Sampling" key="2">
            <Table
              dataSource={record?.detailsRemarks?.batch_sample_value}
              size="small"
              columns={[
                {
                  title: "Ingredients",
                  dataIndex: "material_inventory_name",
                  key: "ingredients",
                  render: (_, record) =>
                    `${record?.material_inventory_name} (${record?.material_inventory_unit})`,
                },
                {
                  title: "Testing Type",
                  dataIndex: "testing_type_name",
                  key: "testing_type",
                },
                {
                  title: "Sample Number",
                  dataIndex: "sample_number",
                  key: "sample_number",
                },
                {
                  title: "Date",
                  dataIndex: "created_on",
                  key: "created_on",
                  render: (created_on) => formatDateTime(created_on),
                },
                {
                  title: "Quantity",
                  dataIndex: "quantity",
                  key: "quantity",
                },
              ]}
              pagination={false}
              bordered
            />
          </Panel>
        )}
        {record?.detailsRemarks?.batch_parameters?.length > 0 && (
          <Panel header="Batch Parameters" key="3">
            <Table
              dataSource={record?.detailsRemarks?.batch_parameters}
              size="small"
              columns={[
                {
                  title: "Date-Time",
                  dataIndex: "created_on",
                  key: "created_on",
                  render: (created_on) => formatDateTime(created_on),
                },
                {
                  title: "Parameter",
                  dataIndex: "parameter_name",
                  key: "parameter_name",
                  render: (_, record) =>
                    `${record?.parameter_name} (${record?.parameter_unit})`,
                },
                {
                  title: "Expected",
                  dataIndex: "quantity",
                  key: "value",
                  align: "center",
                },
                {
                  title: "Min",
                  dataIndex: "min_value",
                  key: "value",
                  align: "center",
                },
                {
                  title: "Max",
                  dataIndex: "max_value",
                  key: "value",
                  align: "center",
                },
                {
                  title: "Actual",
                  dataIndex: "value",
                  key: "value",
                  align: "center",
                },
              ]}
              pagination={false}
              bordered
            />
          </Panel>
        )}
        {record?.detailsRemarks?.batch_product_inventory?.length > 0 && (
          <Panel header="Process Products" key="4">
            <Table
              dataSource={record?.detailsRemarks?.batch_product_inventory}
              size="small"
              columns={[
                {
                  title: "Product",
                  dataIndex: "product_name",
                  key: "product_name",
                },
                {
                  title: "Container No",
                  dataIndex: "Container No",
                  key: "Container No",
                },
                {
                  title: "Tare Wt",
                  dataIndex: "Tare Wt.",
                  key: "Tare Wt.",
                },
                {
                  title: "Gross Wt",
                  dataIndex: "Gross Wt.",
                  key: "Gross Wt.",
                },
                {
                  title: "Net Wt",
                  dataIndex: "Net Wt.",
                  key: "Net Wt.",
                },
              ]}
              pagination={false}
              bordered
            />
          </Panel>
        )}

        {record?.detailsRemarks?.line_clearance_data &&
          record?.detailsRemarks?.line_clearance_data?.length > 0 && (
            <Panel header="Line Clearance" key="4">
              <Table
                size="small"
                pagination={false}
                columns={[
                  {
                    title: "Clearance Type",
                    dataIndex: "clearance_type",
                    key: "clearance_type",
                  },
                  {
                    title: "Expected Value",
                    dataIndex: "expected_value",
                    key: "expected_value",
                  },
                  {
                    title: "Cleaning Types",
                    dataIndex: "cleaning_type_list",
                    key: "cleaning_type_list",
                    render: (types) => {
                      if (!Array.isArray(types)) return null;

                      return (
                        <div className="flex flex-wrap gap-2">
                          {types?.map((t, index) => (
                            <Tag key={index} color="blue">
                              {t?.cleaning_type_name || "NA"}
                            </Tag>
                          ))}
                        </div>
                      );
                    },
                  },

                  {
                    title: "Instruction",
                    dataIndex: "instruction",
                    key: "instruction",
                  },
                  {
                    title: "Remark",
                    dataIndex: "remark",
                    key: "remark",
                    render: (text) => text || "-", // Optional: show dash if no remark
                  },
                ]}
                dataSource={record?.detailsRemarks?.line_clearance_data}
                bordered
                style={{ width: "100%" }}
              />
            </Panel>
          )}
        {record?.detailsRemarks?.api_calculation_data &&
          record?.detailsRemarks?.api_calculation_data?.length > 0 && (
            <Panel header="Api Calculations" key="5">
              <Table
                size="small"
                pagination={false}
                columns={[
                  {
                    title: "Calculation Field",
                    dataIndex: "calculation_field",
                    key: "calculation_field_id",
                  },
                  {
                    title: "Expected Value",
                    dataIndex: "expected_value",
                    key: "expected_value",
                  },
                  {
                    title: "Spec Min",
                    dataIndex: "spec_min",
                    key: "spec_min",
                  },
                  {
                    title: "Spec Max",
                    dataIndex: "spec_max",
                    key: "spec_max",
                  },
                  {
                    title: "Unit",
                    dataIndex: "unit",
                    key: "unit",
                  },
                  {
                    title: "Formula",
                    dataIndex: "formula",
                    key: "formula",
                  },
                  {
                    title: "Instruction",
                    dataIndex: "instruction",
                    key: "instruction",
                  },
                ]}
                dataSource={record?.detailsRemarks?.api_calculation_data}
                bordered
                style={{ width: "100%" }}
              />
            </Panel>
          )}
        {record?.detailsRemarks?.action_list &&
          record?.detailsRemarks?.action_list?.length > 0 && (
            <>
              <Panel header="Action List" key="action_list">
                <Table
                  size="small"
                  pagination={false}
                  columns={[
                    {
                      title: "Label",
                      dataIndex: "label_name",
                      key: "label_name",
                    },
                    {
                      title: "Data Type",
                      dataIndex: "data_type",
                      key: "data_type",
                    },
                    {
                      title: "Additional Data",
                      dataIndex: "additional_data",
                      key: "additional_data",
                      align: "center",
                      render: (add) => (
                        <EyeOutlined
                          className="cursor-pointer text-blue-500 text-lg hover:text-blue-400 hover:scale-110 transition-transform duration-200"
                          onClick={() => showModal(add)}
                        />
                      ),
                    },
                  ]}
                  dataSource={record?.detailsRemarks?.action_list || []}
                  bordered
                  style={{ width: "100%" }}
                />
              </Panel>
            </>
          )}
      </Collapse>
      <ActionCModal
        isOpen={isModalOpen}
        onClose={handleCancel}
        modalData={modalData}
      />
    </div>
  );
};
